import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, Heart, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { Section, Container, Eyebrow, DisplayH1 } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUGGESTIONS = [
  "How do I stop my puppy from biting?",
  "My rescue is fearful of strangers. Where do I start?",
  "How do I teach a rock-solid recall?",
  "Best way to condition a crate?",
];

// Prefer a warm, natural female voice for TONIA
function pickVoice() {
  const voices = window.speechSynthesis?.getVoices() || [];
  const prefer = [
    /Samantha/i, /Google US English/i, /Google UK English Female/i,
    /Karen/i, /Serena/i, /Victoria/i, /Zira/i, /Susan/i, /female/i,
  ];
  for (const rx of prefer) {
    const v = voices.find((x) => rx.test(x.name) || rx.test(x.voiceURI || ""));
    if (v) return v;
  }
  return voices.find((v) => /en/i.test(v.lang)) || voices[0] || null;
}

export default function Assistant() {
  const { user } = useAuth();
  const firstName = user?.name?.split(" ")[0];

  const opener = firstName
    ? `Hello, ${firstName}. I'm TONIA — the Wolfkeeper's AI mentor, named after our founder's mother. I'm so glad you're here. Tell me about your dog, or ask me anything at all. We'll take it one small step at a time.`
    : "Hello there. I'm TONIA — the Wolfkeeper's AI mentor, named after our founder's mother. I care about your dog and about you. Ask me anything — behavior, training, health signals, or which of our programs is the right fit.";

  const [messages, setMessages] = useState([{ role: "assistant", content: opener }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [voiceOn, setVoiceOn] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [tone, setTone] = useState("calm");
  const scrollRef = useRef(null);
  const recogRef = useRef(null);
  const voiceRef = useRef(null);
  const humRef = useRef(null);

  useEffect(() => {
    setMessages((m) => (m.length <= 1 ? [{ role: "assistant", content: opener }] : m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // Setup speech recognition once
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR) {
      const r = new SR();
      r.continuous = false;
      r.interimResults = true;
      r.lang = "en-US";
      r.onresult = (ev) => {
        const t = Array.from(ev.results).map((res) => res[0].transcript).join(" ");
        setInput(t);
      };
      r.onend = () => setListening(false);
      r.onerror = () => { setListening(false); toast.error("Voice input didn't work — try again."); };
      recogRef.current = r;
    }
    // Load voices for TTS
    const loadVoices = () => {
      const v = pickVoice();
      if (v) { voiceRef.current = v; setVoiceReady(true); }
    };
    loadVoices();
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    return () => {
      recogRef.current?.abort?.();
      window.speechSynthesis?.cancel?.();
    };
  }, []);

  const speak = (text) => {
    if (!voiceOn || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    if (voiceRef.current) utter.voice = voiceRef.current;
    if (tone === "encouraging") {
      utter.rate = 1.02;
      utter.pitch = 1.15;
    } else {
      utter.rate = 0.94;
      utter.pitch = 0.98;
    }
    utter.volume = 1;
    utter.onstart = () => { try { humRef.current?.play(); } catch {} };
    utter.onend = () => { try { humRef.current?.pause(); } catch {} };
    window.speechSynthesis.speak(utter);
  };

  const toggleListen = () => {
    if (!recogRef.current) { toast.error("Voice input is not supported in this browser."); return; }
    if (listening) { recogRef.current.stop(); setListening(false); }
    else {
      try { recogRef.current.start(); setListening(true); } catch { setListening(false); }
    }
  };

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    if (!next) window.speechSynthesis?.cancel();
    else toast.success("TONIA will speak her replies aloud.");
  };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const nextMsgs = [...messages, { role: "user", content: q }];
    setMessages(nextMsgs);
    setBusy(true);
    try {
      const r = await api.post("/assistant/chat", { messages: nextMsgs, session_id: sessionId, tone });
      setSessionId(r.data.session_id);
      const answer = r.data.answer;
      setMessages((m) => [...m, { role: "assistant", content: answer }]);
      speak(stripMarkdown(answer));
    } catch (e) {
      toast.error("TONIA is having a moment — please try again.");
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "I'm having trouble reaching my notes right now. Give me a moment and try again." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section testid="assistant-page" className="pt-32 pb-24">
      <Container>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <Eyebrow>Meet TONIA · 24/7 · Voice-ready</Eyebrow>
            </div>
            <DisplayH1 className="mt-6">Meet TONIA.</DisplayH1>
            <p className="mt-4 text-[color:var(--wk-gold)] font-serif-display italic text-xl leading-snug">
              Named in honor of Tonia Sanzone — the mother whose love for dogs started this whole University.
            </p>
            <p className="mt-6 text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
              TONIA is the Wolfkeeper's warm, patient AI mentor. She's calm and authoritative on
              the Method — and she genuinely cares about you and your dog. Now with a real
              spoken voice: talk to her hands-free during training sessions.
            </p>

            <div className="mt-8 hairline rounded-2xl p-5 flex items-start gap-4">
              <Heart className="text-[color:var(--wk-gold)] shrink-0 mt-1" size={18} />
              <div>
                <div className="overline text-[10px]">Her promise</div>
                <p className="mt-1 text-sm text-[color:var(--wk-bone-dim)] leading-relaxed">
                  TONIA only recommends humane, reward-based methodology. For anything urgent
                  or medical, she'll always point you to a qualified veterinary behaviorist.
                </p>
              </div>
            </div>

            <div className="mt-6">
              <div className="overline">Her tone</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {[
                  { key: "calm", label: "Calm", desc: "Steady, grounded, slow" },
                  { key: "encouraging", label: "Encouraging", desc: "Uplifting, celebratory" },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTone(t.key)}
                    data-testid={`assistant-tone-${t.key}`}
                    className={`text-left p-4 rounded-2xl border transition-colors ${
                      tone === t.key
                        ? "border-[color:var(--wk-gold)] bg-[color:var(--wk-forest-tint)]"
                        : "border-white/10 hover:border-[color:var(--wk-gold)]"
                    }`}
                  >
                    <div className={`font-serif-display text-lg ${tone === t.key ? "text-[color:var(--wk-gold)]" : ""}`}>{t.label}</div>
                    <div className="text-[11px] text-[color:var(--wk-bone-dim)] mt-1">{t.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 space-y-2">
              <div className="overline">Try asking</div>
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`assistant-suggestion-${s.slice(0, 10)}`}
                  onClick={() => send(s)}
                  className="w-full text-left p-4 hairline rounded-xl hover:border-[color:var(--wk-gold)] hover:text-[color:var(--wk-gold)] transition-colors text-sm"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="card-lux p-0 overflow-hidden h-[72vh] flex flex-col">
              <div className="p-5 border-b border-white/10 flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full border border-[color:var(--wk-gold)]/70 flex items-center justify-center bg-[color:var(--wk-forest-tint)]">
                    <span className="font-serif-display text-[color:var(--wk-gold)] text-lg leading-none">T</span>
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ${listening ? "bg-red-400 animate-pulse" : "bg-[color:var(--wk-gold)]"} ring-2 ring-black`} />
                </div>
                <div>
                  <div className="text-sm">TONIA</div>
                  <div className="text-xs text-[color:var(--wk-bone-dim)]">
                    {listening ? "Listening…" : voiceOn ? "Voice on · she'll speak her replies" : "Voice off · text only"}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={toggleVoice}
                    aria-label="Toggle TONIA voice"
                    data-testid="assistant-voice-toggle"
                    className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${voiceOn ? "bg-[color:var(--wk-gold)] text-black border-[color:var(--wk-gold)]" : "border-white/15 text-[color:var(--wk-bone)] hover:border-[color:var(--wk-gold)] hover:text-[color:var(--wk-gold)]"}`}
                  >
                    {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
                  </button>
                  <Sparkles size={14} className="text-[color:var(--wk-gold)]" />
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6" data-testid="assistant-thread">
                {messages.map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} gap-3`}
                  >
                    {m.role === "assistant" && (
                      <div className="w-8 h-8 rounded-full border border-[color:var(--wk-gold)]/60 flex items-center justify-center shrink-0 bg-[color:var(--wk-forest-tint)]">
                        <span className="font-serif-display text-[color:var(--wk-gold)] text-sm leading-none">T</span>
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] p-4 rounded-2xl text-[15px] leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-[color:var(--wk-gold)] text-black rounded-tr-sm"
                          : "bg-[#151515] border border-white/10 text-[color:var(--wk-bone)] rounded-tl-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                  </motion.div>
                ))}
                {busy && (
                  <div className="flex justify-start gap-3">
                    <div className="w-8 h-8 rounded-full border border-[color:var(--wk-gold)]/60 flex items-center justify-center shrink-0 bg-[color:var(--wk-forest-tint)]">
                      <span className="font-serif-display text-[color:var(--wk-gold)] text-sm leading-none">T</span>
                    </div>
                    <div className="bg-[#151515] border border-white/10 rounded-2xl p-4 flex gap-1.5 items-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--wk-gold)] animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--wk-gold)] animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--wk-gold)] animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                )}
              </div>

              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-4 border-t border-white/10 flex gap-3 items-center">
                <button
                  type="button"
                  onClick={toggleListen}
                  aria-label={listening ? "Stop listening" : "Speak to TONIA"}
                  data-testid="assistant-mic"
                  className={`w-12 h-12 rounded-full flex items-center justify-center border transition-all shrink-0 ${
                    listening
                      ? "bg-red-500/90 text-white border-red-500 animate-pulse"
                      : "border-white/15 hover:border-[color:var(--wk-gold)] hover:text-[color:var(--wk-gold)]"
                  }`}
                >
                  {listening ? <MicOff size={16} /> : <Mic size={16} />}
                </button>
                <input
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={firstName ? `Tell TONIA anything, ${firstName}…` : "Ask TONIA about your dog…"}
                  className="flex-1 bg-[#0f0f0f] border border-white/10 focus:border-[color:var(--wk-gold)] outline-none rounded-full px-5 py-3 text-[color:var(--wk-bone)] placeholder:text-[color:var(--wk-muted)]"
                />
                <button
                  data-testid="assistant-send"
                  disabled={busy || !input.trim()}
                  className="btn-gold h-12 w-12 p-0 rounded-full disabled:opacity-60 shrink-0"
                  aria-label="Send"
                >
                  <Send size={16} />
                </button>
              </form>
              {(!voiceReady) && voiceOn && (
                <div className="px-4 py-2 text-[11px] text-[color:var(--wk-bone-dim)] border-t border-white/5">Loading voice…</div>
              )}
              {/* Ambient warm hum — plays only during TONIA's speech */}
              <audio
                ref={humRef}
                loop
                preload="auto"
                src="https://cdn.pixabay.com/audio/2022/03/18/audio_c8c8ac6a5c.mp3"
                onLoadedMetadata={(e) => { e.currentTarget.volume = 0.18; }}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}

function stripMarkdown(md) {
  return md
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/#+\s?/g, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/^\s*-\s+/gm, "");
}
